/* eslint-disable @typescript-eslint/no-unused-vars */

import {
  axios,
  AxiosResponsePromise,
  AxiosRequestExtraConfig,
  AxiosRequestConfig,
} from '@seiue/axios'
import {
  createConfigBuilder,
  useQueryApi,
  useMutationApi,
  UseQueryApiReturns,
} from '../utils'
import { MutationOptions, QueryOptions, Expand } from '../types'
import { Operator } from './operator'
import { UserDomainEnum } from './user-domain-enum'
import { LoginReq } from './login-req'
import { User } from './user'
import { RefreshTokenReq } from './refresh-token-req'
import { ReminderReq } from './reminder-req'
import { Reminder } from './reminder'
import { CheckReminder } from './check-reminder'
import { CheckUser } from './check-user'
import { CheckUserExistByPhoneQuery } from './check-user-exist-by-phone-query'

const buildConfig = createConfigBuilder('growp')

export const authApi = {
  checkDataTokenConfig(options?: AxiosRequestExtraConfig): AxiosRequestConfig {
    return buildConfig(options, 'get', '/data_token/check', {})
  },

  /**
   * @summary 获取查询数据平台接口的 token
   *  - 请求成果后，请求接口在 headers 中带上这个 token，示例：x-rbac-token:V1#apisix#token
   *  - 如果为空，说明用户没有权限
   * @param [options]
   * @return AxiosResponsePromise<string>
   */
  checkDataToken(
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<string> {
    return axios(authApi.checkDataTokenConfig(options)) as any
  },

  useCheckDataToken(queryOptions?: QueryOptions): UseQueryApiReturns<string> {
    return useQueryApi<string>(
      authApi.checkDataToken,
      'Auth.checkDataToken',
      queryOptions
    )
  },

  loadReflectionConfig(options?: AxiosRequestExtraConfig): AxiosRequestConfig {
    return buildConfig(options, 'get', '/load/reflection', {})
  },

  /**
   * @summary 查询用户信息
   * @param [options]
   * @return AxiosResponsePromise<Operator>
   */
  loadReflection(
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Operator> {
    return axios(authApi.loadReflectionConfig(options)).then(
      ({ data: _data, ...rest }) => {
        const data = _data ? new Operator(_data) : _data
        return { data, ...rest }
      }
    ) as any
  },

  useLoadReflection(queryOptions?: QueryOptions): UseQueryApiReturns<Operator> {
    return useQueryApi<Operator>(
      authApi.loadReflection,
      'Auth.LoadReflection',
      queryOptions
    )
  },

  loginConfig(
    domain: UserDomainEnum,
    loginReq: LoginReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'post',
      '/login/{domain}',
      {
        domain,
      },
      null,
      loginReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          schoolId: (v: any) => (v === 0 ? v : v || null),
          account: (v: any) => v || '',
          password: (v: any) => v || '',
          phone: (v: any) => v || '',
          reminderId: (v: any) => v || '',
          multiRid: (v: any) => v || 0,
          openLogin: (v: any) => v || null,
        },
      }
    )
  },

  /**
   * @summary 登录
   * @param domain
   * @param loginReq
   * @param [options]
   * @return AxiosResponsePromise<User>
   */
  login(
    domain: UserDomainEnum,
    loginReq: LoginReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<User> {
    return axios(authApi.loginConfig(domain, loginReq, options)).then(
      ({ data: _data, ...rest }) => {
        const data = _data ? new User(_data) : _data
        return { data, ...rest }
      }
    ) as any
  },

  useLogin(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ domain, payload }: { domain: UserDomainEnum; payload: LoginReq }) =>
        authApi.login(domain, payload),
      mutationOptions
    )
  },

  logoutConfig(
    domain: UserDomainEnum,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(options, 'get', '/logout/{domain}', {
      domain,
    })
  },

  /**
   * @summary 登出
   * @param domain
   * @param [options]
   * @return AxiosResponsePromise<null>
   */
  logout(
    domain: UserDomainEnum,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<null> {
    return axios(authApi.logoutConfig(domain, options)) as any
  },

  useLogout(
    domain: UserDomainEnum,
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<null> {
    return useQueryApi<null>(
      authApi.logout,
      'Auth.logout',
      queryOptions,
      domain
    )
  },

  authRefreshTokenConfig(
    refreshTokenReq: RefreshTokenReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'post',
      '/refresh_token/auth',
      {},
      null,
      refreshTokenReq,
      {
        format: 'json',
        isArray: false,
        defaults: {},
      }
    )
  },

  /**
   * @summary 通过 refresh token 获取 token
   * @param refreshTokenReq
   * @param [options]
   * @return AxiosResponsePromise<User>
   */
  authRefreshToken(
    refreshTokenReq: RefreshTokenReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<User> {
    return axios(authApi.authRefreshTokenConfig(refreshTokenReq, options)).then(
      ({ data: _data, ...rest }) => {
        const data = _data ? new User(_data) : _data
        return { data, ...rest }
      }
    ) as any
  },

  useAuthRefreshToken(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (payload: RefreshTokenReq) => authApi.authRefreshToken(payload),
      mutationOptions
    )
  },

  checkRefreshTokenConfig(
    refreshTokenReq: RefreshTokenReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'post',
      '/refresh_token/check',
      {},
      null,
      refreshTokenReq,
      {
        format: 'json',
        isArray: false,
        defaults: {},
      }
    )
  },

  /**
   * @summary 检查 refresh token 是否过期
   * @param refreshTokenReq
   * @param [options]
   * @return AxiosResponsePromise<boolean>
   */
  checkRefreshToken(
    refreshTokenReq: RefreshTokenReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<boolean> {
    return axios(
      authApi.checkRefreshTokenConfig(refreshTokenReq, options)
    ) as any
  },

  useCheckRefreshToken(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (payload: RefreshTokenReq) => authApi.checkRefreshToken(payload),
      mutationOptions
    )
  },

  remindersConfig(
    reminderReq: ReminderReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(options, 'post', '/reminders', {}, null, reminderReq, {
      format: 'json',
      isArray: false,
      defaults: {
        template: (v: any) => v || '',
        activityId: (v: any) => v || 0,
      },
    })
  },

  /**
   * @summary 获取验证码
   * @param reminderReq
   * @param [options]
   * @return AxiosResponsePromise<Reminder>
   */
  reminders(
    reminderReq: ReminderReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Reminder> {
    return axios(authApi.remindersConfig(reminderReq, options)).then(
      ({ data: _data, ...rest }) => {
        const data = _data ? new Reminder(_data) : _data
        return { data, ...rest }
      }
    ) as any
  },

  useReminders(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (payload: ReminderReq) => authApi.reminders(payload),
      mutationOptions
    )
  },

  checkReminderConfig(
    checkReminder: CheckReminder,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'post',
      '/reminders/check',
      {},
      null,
      checkReminder,
      {
        format: 'json',
        isArray: false,
        defaults: {},
      }
    )
  },

  /**
   * @summary 【前台】校验验证码
   * @param checkReminder
   * @param [options]
   * @return AxiosResponsePromise<boolean>
   */
  checkReminder(
    checkReminder: CheckReminder,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<boolean> {
    return axios(authApi.checkReminderConfig(checkReminder, options)) as any
  },

  useCheckReminder(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (payload: CheckReminder) => authApi.checkReminder(payload),
      mutationOptions
    )
  },

  secondCheckReminderConfig(
    rid: number,
    checkReminder: CheckReminder,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'post',
      '/reminders/second-check/{rid}',
      {
        rid,
      },
      null,
      checkReminder,
      {
        format: 'json',
        isArray: false,
        defaults: {},
      }
    )
  },

  /**
   * @summary 【前台】二次校验验证码
   *  - 正式用户使用密码登录+触发二次校验验证码
   * @param rid
   * @param checkReminder
   * @param [options]
   * @return AxiosResponsePromise<User>
   */
  secondCheckReminder(
    rid: number,
    checkReminder: CheckReminder,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<User> {
    return axios(
      authApi.secondCheckReminderConfig(rid, checkReminder, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new User(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useSecondCheckReminder(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ rid, payload }: { rid: number; payload: CheckReminder }) =>
        authApi.secondCheckReminder(rid, payload),
      mutationOptions
    )
  },

  checkUserExistByPhoneConfig(
    domain: UserDomainEnum,
    checkUser: CheckUser,
    checkUserExistByPhoneQuery?: CheckUserExistByPhoneQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'post',
      '/user/{domain}/check',
      {
        domain,
      },
      checkUserExistByPhoneQuery,
      checkUser,
      {
        format: 'json',
        isArray: false,
        defaults: {},
      }
    )
  },

  /**
   * @summary 【前台】根据手机号检查用户是否存在
   * @param domain
   * @param checkUser
   * @param [checkUserExistByPhoneQuery]
   * @param [checkUserExistByPhoneQuery.activityId]
   * @param [options]
   * @return AxiosResponsePromise<boolean>
   */
  checkUserExistByPhone(
    domain: UserDomainEnum,
    checkUser: CheckUser,
    checkUserExistByPhoneQuery?: CheckUserExistByPhoneQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<boolean> {
    return axios(
      authApi.checkUserExistByPhoneConfig(
        domain,
        checkUser,
        checkUserExistByPhoneQuery,
        options
      )
    ) as any
  },

  useCheckUserExistByPhone(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({
        domain,
        payload,
        query,
      }: {
        domain: UserDomainEnum
        payload: CheckUser
        query?: CheckUserExistByPhoneQuery
      }) => authApi.checkUserExistByPhone(domain, payload, query),
      mutationOptions
    )
  },
}

// generate uuid for every api function, useful for result-caching & other things
Object.keys(authApi).forEach((api) => {
  // @ts-ignore
  authApi[api]._name_ = `Auth.${api}`
})
